import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { supabase, getCurrentUser } from '@/lib/supabase';
import { captureEvent, resetAnalytics } from '@/lib/analytics';

/**
 * AuthContext - Manages user authentication state with Supabase
 * 
 * Migrated from Base44 to Supabase for independent authentication.
 * Maintains the same interface to minimize changes to existing components.
 * 
 * Features:
 * - Email/password authentication
 * - Google OAuth (and other providers)
 * - Password reset
 * - Session management
 * - Auto token refresh
 */

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  // isAuthSettled: true only after a real DB read succeeds (not a timeout/cache fallback).
  // Used by Layout to prevent premature RoleSetup redirects when the DB is slow to
  // respond on OAuth redirect and we temporarily have a null-role user in state.
  const [isAuthSettled, setIsAuthSettled] = useState(false);
  const isLoadingUserRef = useRef(false); // Use ref for synchronous lock
  const userRef = useRef(null); // Stable ref so event handlers don't need user in deps
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(false); // Not needed with Supabase
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings, setAppPublicSettings] = useState(null); // Kept for compatibility
  const lastRoleUpdateRef = useRef(null); // Track when role was last updated

  // Debounce timer for refreshUserRole to prevent excessive calls
  const refreshUserRoleTimerRef = useRef(null);
  
  // Function to refresh user data and check for role changes
  const refreshUserRole = async () => {
    if (!user?.id) return;
    
    // Clear any pending refresh calls (debounce)
    if (refreshUserRoleTimerRef.current) {
      clearTimeout(refreshUserRoleTimerRef.current);
    }
    
    // Debounce: wait 500ms before actually calling
    refreshUserRoleTimerRef.current = setTimeout(async () => {
      try {
        console.log('🔄 Refreshing user role...');
        const { data, error } = await supabase
          .from('users')
          .select('user_role, full_name, phone, neighborhood, profile_photo, estate_id')
          .eq('id', user.id)
          .single();
        
        if (error) {
          console.error('Error refreshing user role:', error);
          return;
        }

        if (data && data.user_role !== user.user_role) {
          console.log('🔔 Role changed detected:', user.user_role, '->', data.user_role);
          
          // Update user with new role
          const updatedUser = {
            ...user,
            ...data
          };
          
          setUser(updatedUser);
          
          // Update localStorage cache
          localStorage.setItem(`user_role_${user.id}`, data.user_role);
          
           // Check if this role change happened very recently (within 5 seconds)
          // If so, it's likely from RoleSetup and we should skip reload to let navigation complete
          const now = Date.now();
          const lastUpdate = lastRoleUpdateRef.current || 0;
          const timeSinceLastUpdate = now - lastUpdate;
          
          // Update the timestamp
          lastRoleUpdateRef.current = now;
          
          if (timeSinceLastUpdate < 5000 && lastUpdate > 0) {
            // Role was updated very recently - skip reload, let navigation happen
            console.log('⏭️ Skipping reload - role just updated, letting navigation complete');
          } else {
            // Role changed independently (admin change, etc) - reload to apply permissions
            console.log('💫 Reloading to apply new permissions');
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Error in refreshUserRole:', error);
      }
    }, 500);
  };

  // Cleanup function for debounce timer
  useEffect(() => {
    return () => {
      if (refreshUserRoleTimerRef.current) {
        clearTimeout(refreshUserRoleTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Mount-only: set up auth subscription and kick off initial session check.
    // This must NOT re-run when user state changes — re-subscribing causes the new
    // listener to fire INITIAL_SESSION immediately, which triggers another loadUser
    // call and creates an infinite cascade of DB queries + state updates.
    checkAuth();

    // Set up periodic role checking (every 30 seconds)
    const roleCheckInterval = setInterval(() => {
      if (userRef.current?.id) {
        refreshUserRole();
      }
    }, 30000); // Check every 30 seconds

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔔 Auth state changed:', event);
      
      try {
        if (event === 'SIGNED_IN' && session) {
          console.log('🔐 User signed in, loading user data...');
          await loadUser(session.user);
          setIsLoadingAuth(false); // Critical: end loading state after OAuth
          console.log('✅ Sign-in complete');
        } else if (event === 'INITIAL_SESSION') {
          if (session) {
            console.log('🔐 Initial session detected, loading user data...');
            const ran = await loadUser(session.user);
            // Only end loading if WE actually ran loadUser.
            // If SIGNED_IN is already in progress (OAuth redirect), it holds the lock
            // and will call setIsLoadingAuth(false) when it finishes.
            if (ran !== false) {
              setIsLoadingAuth(false);
            }
            console.log('✅ Initial session loaded');
          } else {
            // No active session — user is not logged in, stop showing loading state.
            console.log('ℹ️ No active session (INITIAL_SESSION)');
            setIsLoadingAuth(false);
          }
        } else if (event === 'PASSWORD_RECOVERY' && session) {
          console.log('🔑 Password recovery session detected');
          console.log('📧 Recovery session for user:', session.user?.email);
          // DO NOT set isAuthenticated or load user - we're in password recovery mode
          // The session exists only for updatePassword to work
          // Don't treat this as a full sign-in
          setIsLoadingAuth(false);
          console.log('✅ Password recovery session ready (NOT authenticated)');
        } else if (event === 'SIGNED_OUT') {
          console.log('👋 User signed out');
          // Clear all localStorage cache when signed out.
          // Use userRef (not user from closure) because this handler is created
          // once on mount and would otherwise capture a stale user = null.
          const userId = userRef.current?.id;
          if (userId) {
            localStorage.removeItem(`user_role_${userId}`);
            localStorage.removeItem(`user_estate_id_${userId}`);
            console.log('🧹 Cleared cached data on SIGNED_OUT');
          }
          userRef.current = null;
          setIsAuthSettled(false);
          setUser(null);
          setIsAuthenticated(false);
          setIsLoadingAuth(false);
          resetAnalytics();
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('🔄 Token refreshed');
          if (session?.user) {
            await loadUser(session.user);
          }
        } else if (event === 'USER_UPDATED') {
          console.log('👤 User updated');
          if (session?.user) {
            await loadUser(session.user);
          }
        }
      } catch (error) {
        console.error('❌ Error in auth state change handler:', {
          event,
          error: error.message,
          stack: error.stack
        });
        setAuthError(error.message);
        setIsLoadingAuth(false);
      }
    });

    // Cleanup subscription and interval
    return () => {
      console.log('🧹 Cleaning up auth subscription and role check interval');
      subscription.unsubscribe();
      clearInterval(roleCheckInterval);
    };
  }, []); // mount-only — must NOT depend on user state

  const checkAuth = async () => {
    try {
      console.log('🔍 Checking auth...');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('❌ Error getting session:', sessionError);
        throw sessionError;
      }
      
      if (session?.user) {
        console.log('✅ Session found, loading user...');
        const ran = await loadUser(session.user);
        // If loadUser was blocked (SIGNED_IN auth-change handler is already loading),
        // do NOT call setIsLoadingAuth(false) here — that handler will call it when done.
        if (ran !== false) {
          setIsLoadingAuth(false);
        }
      } else {
        console.log('ℹ️ No session found');
        setIsLoadingAuth(false);
      }
    } catch (error) {
      console.error('❌ Auth check error:', error);
      setAuthError(error.message);
      setIsLoadingAuth(false);
    }
  };

  const loadUser = async (supabaseUser, retryCount = 0) => {
    // Prevent concurrent loadUser calls using ref for synchronous check.
    // Returns false when skipped so callers know not to setIsLoadingAuth(false).
    if (isLoadingUserRef.current) {
      console.log('⏳ Already loading user, skipping duplicate call');
      return false;
    }
    
    isLoadingUserRef.current = true;
    
    try {
      console.log('👤 Loading user data for:', supabaseUser.email);
      console.log('🔍 Querying users table...');
      
      // 10 second timeout to handle slow connections
      const timeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Query timeout after 10s")), 10000)
      );
      
      // Get additional user data from your database (select only needed fields for performance)
      const queryPromise = supabase
        .from("users")
        .select("id, email, full_name, user_role, estate_id, phone, neighborhood, profile_photo, notification_preferences, deleted_at, created_at, updated_at")
        .eq("id", supabaseUser.id)
        .single();
      
      const result = await Promise.race([queryPromise, timeout])
        .catch(err => {
          console.warn('⚠️ Database query slow/timed out:', err.message);
          return { data: null, error: { code: 'TIMEOUT', message: err.message } };
        });

      const { data: userData, error } = result;
      console.log('📊 Database query result:', { userData, error });

      // Check if this is a deleted user trying to sign back in
      if (userData && userData.deleted_at !== null) {
        console.log('🔄 Deleted user signing back in, resetting account...');
        
        // Reset the user to a fresh state
        const { error: resetError } = await supabase
          .from('users')
          .update({
            email: supabaseUser.email,
            full_name: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || 'User',
            user_role: null,  // Force role selection
            estate_id: null,
            phone: null,
            profile_photo: null,
            neighborhood: null,
            notification_preferences: null,
            deleted_at: null,  // Mark as active
            updated_at: new Date().toISOString(),
            created_at: new Date().toISOString()
          })
          .eq('id', supabaseUser.id);
        
        if (resetError) {
          console.error('❌ Error resetting deleted user:', resetError);
        } else {
          console.log('✅ Deleted user reset successfully');
          // Clear any cached data
          localStorage.removeItem(`user_role_${supabaseUser.id}`);
          localStorage.removeItem(`user_estate_id_${supabaseUser.id}`);
          
          // Reload the user data
          return loadUser(supabaseUser, retryCount + 1);
        }
      }

      // If user not found in database (PGRST116 or 406), only retry once for fresh signups
      if (error && (error.code === 'PGRST116' || error.code === 'TIMEOUT' || error.code === '406')) {
        console.log('⚠️ User not found or query timed out');
        
        // Only retry once with 1 second delay (for fresh signups where trigger hasn't completed)
        if (retryCount < 1) {
          console.log(`🔄 Retrying loadUser in 1s (attempt ${retryCount + 1}/1)...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          // Release the lock before the recursive retry so it isn't blocked by itself.
          isLoadingUserRef.current = false;
          return loadUser(supabaseUser, retryCount + 1);
        }
        
        // If we already have user data loaded, keep it completely (including estate_id)
        if (user && user.id === supabaseUser.id) {
          console.log('✅ Keeping existing user data with estate_id:', user.email, user.estate_id);
          // Don't call setUser - just return to keep existing state
          return;
        }
        
        // Try to get cached data from localStorage
        const cachedRole = localStorage.getItem(`user_role_${supabaseUser.id}`);
        const cachedEstateId = localStorage.getItem(`user_estate_id_${supabaseUser.id}`);
        
        // Convert string 'null' back to actual null
        const actualRole = (!cachedRole || cachedRole === 'null') ? null : cachedRole;
        const actualEstateId = (!cachedEstateId || cachedEstateId === 'null') ? null : cachedEstateId;
        
        console.log('📦 Cached data:', { role: actualRole || 'none', estate_id: actualEstateId || 'none' });
        
        // Otherwise, use the auth data as fallback
        // For NEW users (no cached role), set user_role to null to force role setup
        const profilePhoto = supabaseUser.user_metadata?.avatar_url 
          || supabaseUser.user_metadata?.picture 
          || '';
        
        const fullUser = {
          id: supabaseUser.id,
          email: supabaseUser.email,
          full_name: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || '',
          user_role: actualRole,  // null for new users forces role setup
          estate_id: actualEstateId,
          profile_photo: profilePhoto,
          phone: '',
          neighborhood: '',
          created_at: supabaseUser.created_at,
        };
        
        console.log('📸 Profile photo (cached fallback):', profilePhoto);

        console.log('✅ User loaded (from auth data fallback):', fullUser.email);
        setUser(fullUser);
        setIsAuthenticated(true);
        return;
      } else if (error) {
        console.error('❌ Error loading user data from database:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });

        // If we already have a valid user loaded (same ID, has a role), don't
        // overwrite it with a null-role fallback — this happens on the second
        // checkAuth() run triggered by the useEffect([user?.id]) re-render.
        if (user?.id === supabaseUser.id && user?.user_role) {
          console.log('⚠️ DB error on reload but user already loaded correctly, keeping existing state');
          return;
        }
        
        // Still set user with basic data from auth even if database query fails
        // Use null for user_role to force role setup for new users
        const profilePhoto = supabaseUser.user_metadata?.avatar_url 
          || supabaseUser.user_metadata?.picture 
          || '';
        
        const basicUser = {
          id: supabaseUser.id,
          email: supabaseUser.email,
          full_name: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || '',
          user_role: null,  // Force role setup for new users
          profile_photo: profilePhoto,
          phone: '',
          neighborhood: '',
          created_at: supabaseUser.created_at,
        };
        
        console.log('📸 Profile photo (error fallback):', profilePhoto);
        
        console.log('⚠️ Using basic user data from auth:', basicUser.email);
        setUser(basicUser);
        setIsAuthenticated(true);
        return;
      }

      // Combine Supabase auth user with custom user data
      // Determine profile photo with proper fallback chain
      const profilePhoto = userData?.profile_photo 
        || supabaseUser.user_metadata?.avatar_url 
        || supabaseUser.user_metadata?.picture 
        || '';
      
      const fullUser = {
        ...userData, // Start with all database fields
        id: supabaseUser.id,
        email: supabaseUser.email,
        full_name: userData?.full_name || supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || '',
        user_role: userData?.user_role || null,  // Preserve null from database for new users
        estate_id: userData?.estate_id || null,
        profile_photo: profilePhoto, // Apply after spread to ensure it's not overwritten
        phone: userData?.phone || '',
        neighborhood: userData?.neighborhood || '',
        created_at: supabaseUser.created_at,
      };

      console.log('📸 Profile photo sources:');
      console.log('  - Database:', userData?.profile_photo || '(empty)');
      console.log('  - OAuth avatar_url:', supabaseUser.user_metadata?.avatar_url || '(empty)');
      console.log('  - OAuth picture:', supabaseUser.user_metadata?.picture || '(empty)');
      console.log('  - FINAL USED:', profilePhoto || '(empty)');

      // Cache the user role and estate_id in localStorage for fallback
      // Don't store null values - just remove the key
      if (fullUser.user_role) {
        localStorage.setItem(`user_role_${supabaseUser.id}`, fullUser.user_role);
      } else {
        localStorage.removeItem(`user_role_${supabaseUser.id}`);
      }
      
      if (fullUser.estate_id) {
        localStorage.setItem(`user_estate_id_${supabaseUser.id}`, fullUser.estate_id);
      } else {
        localStorage.removeItem(`user_estate_id_${supabaseUser.id}`);
      }

      console.log('✅ User loaded successfully:', fullUser.email, 'estate_id:', fullUser.estate_id);

      // Google OAuth: persist TOS acceptance stored before the OAuth redirect.
      // Stored in Supabase auth metadata — requires no extra DB column.
      // Only applies to brand-new users (user_role is null = hasn't done RoleSetup yet).
      const pendingTos = sessionStorage.getItem('pending_tos_acceptance');
      if (pendingTos && !fullUser.user_role) {
        try {
          await supabase.auth.updateUser({ data: { terms_accepted_at: pendingTos } });
          sessionStorage.removeItem('pending_tos_acceptance');
          console.log('✅ TOS acceptance recorded for Google OAuth user');
        } catch (tosError) {
          console.warn('⚠️ Could not record TOS acceptance:', tosError.message);
          // Non-fatal — don't block the signup flow
        }
      }

      userRef.current = fullUser;
      setIsAuthSettled(true); // Real DB read succeeded — Layout can now make routing decisions
      setUser(fullUser);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('❌ Unexpected error in loadUser:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      setAuthError(error.message);
    } finally {
      isLoadingUserRef.current = false;
    }
  };

  const logout = async (shouldRedirect = true) => {
    try {
      console.log('👋 Logging out...');
      
      // Clear state immediately
      setUser(null);
      setIsAuthenticated(false);
      resetAnalytics();
      
      // Clear all cached user data from localStorage
      if (user?.id) {
        localStorage.removeItem(`user_role_${user.id}`);
        localStorage.removeItem(`user_estate_id_${user.id}`);
        console.log('🧹 Cleared cached user data');
      }
      
      // Sign out from Supabase (this will trigger SIGNED_OUT event)
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      
      if (error) {
        console.error('❌ Logout error:', error);
        // Don't throw - still redirect even if signOut fails
      }
      
      console.log('✅ Logout successful');
      
      // Force redirect to login page
      if (shouldRedirect) {
        // Use replace to prevent back button issues
        window.location.replace('/Login');
      }
    } catch (error) {
      console.error('❌ Logout failed:', {
        message: error.message,
        stack: error.stack
      });
      // Still redirect to login even on error
      window.location.replace('/Login');
    }
  };

  const navigateToLogin = () => {
    window.location.href = '/Login';
  };

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // User data will be loaded by the onAuthStateChange listener
      await loadUser(data.user);
      captureEvent('auth_login_success', {
        method: 'password',
      });

      return data;
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error(error.message || 'Login failed. Please check your credentials.');
    }
  };

  const signup = async (email, password, full_name) => {
    try {
      console.log('📝 Attempting signup for:', email);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: full_name,
          },
          emailRedirectTo: `${window.location.origin}/RoleSetup`
        }
      });

      if (error) {
        console.error('❌ Signup error:', {
          code: error.status,
          message: error.message
        });
        throw error;
      }

      // Check if email confirmation is required
      if (data.user && !data.session) {
        console.log('📧 Email confirmation required for:', email);
        console.log('⚠️ User needs to confirm email before session is created');
        throw new Error('Please check your email to confirm your account before signing in.');
      }

      console.log('✅ Signup successful! Database trigger will create user profile automatically.');
      
      // User profile is created automatically by database trigger
      // Just load the user data if we have a session
      if (data.user && data.session) {
        await loadUser(data.user);
      }

      captureEvent('auth_signup_success', {
        method: 'password',
      });

      return data;
    } catch (error) {
      console.error('❌ Signup failed:', {
        message: error.message,
        stack: error.stack
      });
      setAuthError(error.message);
      throw new Error(error.message || 'Signup failed. Please try again.');
    }
  };

  const loginWithGoogle = async () => {
    try {
      console.log('🔐 Attempting Google OAuth login...');
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/EstateDashboard`,
        },
      });

      if (error) {
        console.error('❌ Google OAuth error:', {
          code: error.status,
          message: error.message
        });
        throw error;
      }

      console.log('✅ Google OAuth redirect initiated');
      captureEvent('auth_login_started', {
        method: 'google_oauth',
      });
      // The redirect will happen automatically
      return data;
    } catch (error) {
      console.error('❌ Google login failed:', {
        message: error.message,
        stack: error.stack
      });
      setAuthError(error.message);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      console.log('🔑 Sending password reset email to:', email);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        console.error('❌ Password reset error:', {
          code: error.status,
          message: error.message
        });
        throw error;
      }
      
      console.log('✅ Password reset email sent');
      captureEvent('auth_password_reset_requested');
    } catch (error) {
      console.error('❌ Password reset failed:', {
        message: error.message,
        stack: error.stack
      });
      setAuthError(error.message);
      throw new Error(error.message || 'Password reset failed. Please try again.');
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      console.log('🔑 Updating password...');
      
      // Check if we have a valid session first
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('❌ Session check error:', sessionError);
        throw new Error('Unable to verify session. Please request a new password reset link.');
      }
      
      if (!session) {
        console.error('❌ No active session found');
        throw new Error('Your password reset link may have expired. Please request a new one.');
      }
      
      console.log('✅ Session valid, updating password...');
      
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('❌ Password update error:', {
          code: error.status,
          message: error.message
        });
        throw error;
      }
      
      console.log('✅ Password updated successfully');
    } catch (error) {
      console.error('❌ Password update failed:', {
        message: error.message,
        stack: error.stack
      });
      throw new Error(error.message || 'Password update failed. Please try again.');
    }
  };

  // Helper function to mark that role is being updated (prevents auto-reload)
  const markRoleUpdating = () => {
    console.log('📝 Marking role as being updated');
    lastRoleUpdateRef.current = Date.now();
  };

  // Helper function to immediately update user state (for mutations that change user data)
  const updateUserState = (updates) => {
    if (!user) return;
    console.log('💾 Updating user state immediately:', updates);
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    
    // Update localStorage cache if role changed
    if (updates.user_role) {
      localStorage.setItem(`user_role_${user.id}`, updates.user_role);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      logout,
      navigateToLogin,
      login,
      signup,
      loginWithGoogle,
      resetPassword,
      updatePassword,
      refreshUserRole, // Export for manual refresh
      checkAppState: checkAuth, // Renamed for compatibility
      isAuthSettled,  // true only after first successful DB read; gates RoleSetup redirect
      markRoleUpdating, // NEW: Signal that role is being updated to prevent reload
      updateUserState, // NEW: Immediately update user state for mutations
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
